import {
  CHANGE_READINESS,
  CHANGE_READINESS_LABELS,
  SCORE_KEY_LABELS_DE,
  SCORE_KEYS,
  WORK_LOCATION_LABELS,
  WORK_LOCATION_PREFERENCES,
  type CandidateFilters,
  type ChangeReadiness,
  type ScoreKey,
  type WorkLocationPreference,
} from "@vidario/domain";
import { Button, Input, Slider } from "@vidario/ui";
import { X } from "lucide-react";
import { type ReactElement } from "react";

const MAX_DISTANCE = 500;

export interface CandidateFiltersPanelProps {
  filters: CandidateFilters;
  /** False when the job has no coordinates — the radius control is then
   * disabled and explained, because no candidate has a distance to filter on.
   * Defaults to true so callers without a job keep the control. */
  distanceAvailable?: boolean;
  /** Aussortiert: the minimum applies to the Vorauswahl score; rows without
   * one are hidden by it (#186). Only it and the radius are shown (#200). */
  vorauswahl?: boolean;
  onChange: (next: CandidateFilters) => void;
  onReset: () => void;
  onClose: () => void;
}

/** Inline filter panel — lives as a sibling of the candidate grid, not
 * as a Radix Dialog overlay. The recruiter sees the live-filtered list
 * and the slider state side-by-side as they drag. Sticky-positioned so
 * it stays visible while scrolling through long candidate lists.
 *
 * Same form content as the earlier Drawer variant; only the chrome
 * differs (no Portal, no backdrop, no focus trap). */
export function CandidateFiltersPanel({
  filters,
  distanceAvailable = true,
  vorauswahl = false,
  onChange,
  onReset,
  onClose,
}: CandidateFiltersPanelProps): ReactElement {
  // exactOptionalPropertyTypes forbids `field: undefined` on optional
  // props, so every "clear this filter" path goes through omit().
  const setSubScore = (key: ScoreKey, value: number | undefined): void => {
    const minSubScores = { ...(filters.minSubScores ?? {}) };
    if (value === undefined || value === 0) delete minSubScores[key];
    else minSubScores[key] = value;
    if (Object.keys(minSubScores).length === 0) {
      onChange(omit(filters, "minSubScores"));
    } else {
      onChange({ ...filters, minSubScores });
    }
  };

  const setMaxDistance = (value: number | undefined): void => {
    if (value === undefined || value >= MAX_DISTANCE) {
      onChange(omit(filters, "maxDistanceKm"));
    } else {
      onChange({ ...filters, maxDistanceKm: value });
    }
  };

  const setMinScore = (value: number | undefined): void => {
    if (value === undefined || value === 0) {
      onChange(omit(filters, "minScore"));
    } else {
      onChange({ ...filters, minScore: value });
    }
  };

  const toggleWorkLocation = (loc: WorkLocationPreference): void => {
    const current = new Set(filters.workLocations ?? []);
    if (current.has(loc)) current.delete(loc);
    else current.add(loc);
    if (current.size === 0) onChange(omit(filters, "workLocations"));
    else onChange({ ...filters, workLocations: Array.from(current) });
  };

  const toggleChangeReadiness = (val: ChangeReadiness): void => {
    const current = new Set(filters.changeReadiness ?? []);
    if (current.has(val)) current.delete(val);
    else current.add(val);
    if (current.size === 0) onChange(omit(filters, "changeReadiness"));
    else onChange({ ...filters, changeReadiness: Array.from(current) });
  };

  const setSalary = (which: "min" | "max", value: number | undefined): void => {
    const key = which === "min" ? "salaryMin" : "salaryMax";
    if (value === undefined) onChange(omit(filters, key));
    else onChange({ ...filters, [key]: value });
  };

  const setMustHavesOnly = (on: boolean): void => {
    if (on) onChange({ ...filters, mustHavesOnly: true });
    else onChange(omit(filters, "mustHavesOnly"));
  };

  return (
    <aside
      id="candidate-filters-panel"
      // `sticky top-6` keeps the panel pinned while the candidate list
      // scrolls; `max-h-[calc(100vh-3rem)]` caps its height so it scrolls
      // internally instead of disappearing off-screen on short windows.
      className="flex w-full flex-col lg:sticky lg:top-6 lg:order-last lg:max-h-[calc(100vh-3rem)] lg:w-[380px] lg:shrink-0 rounded-xl border border-border-default bg-neutral-0 shadow-sm"
      aria-label="Kandidaten-Filter"
    >
      <header className="flex items-center justify-between border-b border-border-default px-5 py-3.5">
        <h2 className="m-0 text-base font-semibold">Filter</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Filter ausblenden"
          className="inline-flex rounded-md p-1.5 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
        >
          <X size={18} />
        </button>
      </header>

      <div className="flex-1 overflow-auto px-5 py-5">
        <div className="flex flex-col gap-7">
          <FilterSection
            title={vorauswahl ? "Vorauswahl" : "Score-Schwellen"}
            hint={
              vorauswahl
                ? "Passung von Jobtitel und Ort. Ab einem Wert über 0 werden Kandidaten mit früherem Score ausgeblendet."
                : "Untergrenzen — alles darunter wird ausgeblendet."
            }
          >
            <SliderRow
              label={vorauswahl ? "Vorauswahl ab" : "Mindest-Gesamt-Score"}
              value={filters.minScore ?? 0}
              displayValue={
                vorauswahl ? String(filters.minScore ?? 0) : `${filters.minScore ?? 0}%`
              }
              onChange={(v) => setMinScore(v)}
            />
            {/* Aussortiert rows have no AI sub-scores (#200). */}
            {!vorauswahl &&
              SCORE_KEYS.map((key) => (
                <SliderRow
                  key={key}
                  label={`Mindest-Score: ${SCORE_KEY_LABELS_DE[key]}`}
                  value={filters.minSubScores?.[key] ?? 0}
                  displayValue={`${filters.minSubScores?.[key] ?? 0}%`}
                  onChange={(v) => setSubScore(key, v)}
                />
              ))}
          </FilterSection>

          <FilterSection
            title="Entfernung"
            hint={
              distanceAvailable
                ? "Luftlinie (nicht Wegstrecke). Kandidaten über dem Radius werden ausgeblendet — ebenso Kandidaten ohne Wohnort."
                : "Für diese Stelle konnte kein Standort bestimmt werden — Entfernungen lassen sich nicht berechnen. Ergänze den Ort in der Stelle und starte die Kandidatensuche neu."
            }
          >
            <SliderRow
              label="Max. Entfernung"
              value={filters.maxDistanceKm ?? MAX_DISTANCE}
              displayValue={
                !distanceAvailable
                  ? "Nicht verfügbar"
                  : filters.maxDistanceKm === undefined
                    ? "Kein Limit"
                    : `${filters.maxDistanceKm} km`
              }
              min={0}
              max={MAX_DISTANCE}
              step={5}
              disabled={!distanceAvailable}
              onChange={(v) => setMaxDistance(v)}
            />
          </FilterSection>

          {/* Skills, salary, Arbeitsort and Wechselbereitschaft aren't known for
              Aussortiert rows; these filters would empty the tab (#200). */}
          {!vorauswahl && (
            <>
              <FilterSection
                title="Must-Have Knockout"
                hint="Blendet Kandidaten aus, denen Pflicht-Kriterien der Stelle fehlen."
              >
                <label className="flex items-center gap-3 text-sm">
                  <input
                    type="checkbox"
                    checked={Boolean(filters.mustHavesOnly)}
                    onChange={(e) => setMustHavesOnly(e.target.checked)}
                    className="h-4 w-4 rounded border-border-default text-primary-500 focus:ring-primary-500"
                  />
                  <span>Nur Kandidaten mit allen Must-Haves anzeigen</span>
                </label>
              </FilterSection>

              <FilterSection
                title="Gehaltsrange (€)"
                hint="Schnittmenge mit der Kandidaten-Range; unbekannte Werte gelten als offen."
              >
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    placeholder="von"
                    value={filters.salaryMin?.toString() ?? ""}
                    onChange={(e) => {
                      const n = Number.parseInt(e.target.value, 10);
                      setSalary("min", Number.isFinite(n) ? n : undefined);
                    }}
                    aria-label="Gehalt von"
                  />
                  <span className="text-text-tertiary">bis</span>
                  <Input
                    type="number"
                    placeholder="bis"
                    value={filters.salaryMax?.toString() ?? ""}
                    onChange={(e) => {
                      const n = Number.parseInt(e.target.value, 10);
                      setSalary("max", Number.isFinite(n) ? n : undefined);
                    }}
                    aria-label="Gehalt bis"
                  />
                </div>
              </FilterSection>

              <FilterSection title="Arbeitsort">
                <div className="flex flex-wrap gap-2">
                  {WORK_LOCATION_PREFERENCES.map((loc) => {
                    const active = filters.workLocations?.includes(loc) ?? false;
                    return (
                      <Button
                        key={loc}
                        variant={active ? "primary" : "secondary"}
                        size="sm"
                        onClick={() => toggleWorkLocation(loc)}
                      >
                        {WORK_LOCATION_LABELS[loc]}
                      </Button>
                    );
                  })}
                </div>
              </FilterSection>

              <FilterSection title="Wechselbereitschaft">
                <div className="flex flex-wrap gap-2">
                  {CHANGE_READINESS.map((val) => {
                    const active = filters.changeReadiness?.includes(val) ?? false;
                    return (
                      <Button
                        key={val}
                        variant={active ? "primary" : "secondary"}
                        size="sm"
                        onClick={() => toggleChangeReadiness(val)}
                      >
                        {CHANGE_READINESS_LABELS[val]}
                      </Button>
                    );
                  })}
                </div>
              </FilterSection>
            </>
          )}
        </div>
      </div>

      <footer className="flex justify-between gap-3 border-t border-border-default px-5 py-3">
        <Button variant="secondary" size="sm" onClick={onReset}>
          Alle zurücksetzen
        </Button>
        <Button size="sm" onClick={onClose}>
          Schließen
        </Button>
      </footer>
    </aside>
  );
}

interface FilterSectionProps {
  title: string;
  hint?: string;
  children: React.ReactNode;
}

function FilterSection({ title, hint, children }: FilterSectionProps): ReactElement {
  return (
    <section className="flex flex-col gap-3">
      <div>
        <div className="text-sm font-semibold">{title}</div>
        {hint && <div className="mt-0.5 text-xs text-text-tertiary">{hint}</div>}
      </div>
      {children}
    </section>
  );
}

function omit<T extends object, K extends keyof T>(obj: T, ...keys: readonly K[]): Omit<T, K> {
  const result = { ...obj };
  for (const k of keys) delete result[k];
  return result;
}

interface SliderRowProps {
  label: string;
  value: number;
  displayValue: string;
  onChange: (value: number | undefined) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
}

function SliderRow({
  label,
  value,
  displayValue,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  disabled = false,
}: SliderRowProps): ReactElement {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between text-xs">
        <span className="text-text-secondary">{label}</span>
        <span className="font-medium text-text-primary">{displayValue}</span>
      </div>
      <Slider
        value={[value]}
        onValueChange={(next) => {
          const [v] = next;
          onChange(typeof v === "number" ? v : undefined);
        }}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        ariaLabel={label}
      />
    </div>
  );
}
